import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import TablePagination from "./TablePagination";
import ReportsApi from "../Api/ReportsApi";
import ProductApi from "../Api/ProductApi";
import StandardDatePicker from "./StandardDatePicker";
import usePermissions from "../hook/usePermissions";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const StockReportsLayer = () => {
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Default dates: this month's starting date and today
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState("");
    const [loadingProducts, setLoadingProducts] = useState(false);

    const { hasPermission } = usePermissions();

    const [productPage, setProductPage] = useState(0);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);

    const fetchProductOptions = async (search = "", pageNum = 0, append = false) => {
        setLoadingProducts(true);
        try {
            const limit = 200;
            const res = await ProductApi.getAll(pageNum, limit, search);
            if (res.status && res.response && res.response.data) {
                const fetchedData = res.response.data || [];
                const total = res.response.total || 0;
                const fetchedOpts = fetchedData.map(p => ({ value: p._id, label: p.name }));

                setHasMoreProducts((pageNum + 1) * limit < total && fetchedData.length === limit);

                setProductOptions(prev => {
                    const base = append ? [...prev, ...fetchedOpts] : fetchedOpts;
                    const map = new Map();
                    base.forEach(o => map.set(o.value, o));
                    if (selectedProduct && !map.has(selectedProduct.value)) {
                        map.set(selectedProduct.value, selectedProduct);
                    }
                    return Array.from(map.values());
                });
                setProductPage(pageNum);
            }
        } catch (error) {
            console.error("Error fetching products for dropdown:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleMenuScrollToBottom = () => {
        if (hasMoreProducts && !loadingProducts) {
            fetchProductOptions(productSearch, productPage + 1, true);
        }
    };

    useEffect(() => {
        fetchProductOptions("", 0, false);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchProductOptions(productSearch, 0, false);
        }, 300);
        return () => clearTimeout(timer);
    }, [productSearch]);

    useEffect(() => {
        fetchReport();
    }, [currentPage, rowsPerPage, selectedProduct, fromDate, toDate]);

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: rowsPerPage,
                productId: selectedProduct?.value || undefined,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined
            };
            const res = await ReportsApi.getStockReports(params);
            if (res.status) {
                setReportData(res.response.data || []);
                setTotalRecords(res.response.total || 0);
                setTotalPages(Math.ceil((res.response.total || 0) / rowsPerPage));
            }
        } catch (error) {
            console.error("Error fetching stock report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setSelectedProduct(null);
        setProductSearch("");
        fetchProductOptions("");
        setCurrentPage(0);
    };

    const getVariantText = (item) => {
        const raw = item.variantLabel || item.attributeId || "-";
        if (typeof raw === "string") {
            return raw.replace(/(?:^|,\s*)[^:,]+:\s*/g, "");
        }
        return raw;
    };

    const getReferenceText = (item) => {
        const orderId = item.orderData?.[0]?.orderId || item.orderData?.[0]?.invoiceId;
        if (orderId) return orderId;

        const checkRef = item.referenceLabel || item.referenceId;
        if (checkRef && checkRef !== "N/A") {
            const str = String(checkRef).trim();
            if (/^[0-9a-fA-F]{24}$/.test(str)) {
                return `Order #${str.slice(-6).toUpperCase()} (Deleted)`;
            }
            return str;
        }

        if (item.description && item.description.trim()) return item.description;

        return "N/A";
    };

    const handleExportExcel = async () => {
        toast.info("Preparing Excel file...");
        try {
            const params = { page: 0, limit: -1, productId: selectedProduct?.value || undefined, fromDate: fromDate || undefined, toDate: toDate || undefined };
            const res = await ReportsApi.getStockReports(params);
            if (res.status && res.response.data) {
                const data = res.response.data.map((item, index) => ({
                    "S.No": index + 1,
                    "Date": item.createdAt ? new Date(item.createdAt).toLocaleString() : "-",
                    "Product": item.productName || "-",
                    "Variant": getVariantText(item),
                    "Prev Stock": item.previousStock || 0,
                    "Change": item.quantity > 0 ? `+${item.quantity}` : item.quantity,
                    "Current Stock": item.currentStock || 0,
                    "Type": (item.type || "Manual").toUpperCase(),
                    "Reference": getReferenceText(item),
                    "Created By": item.createdByName || "System",
                    "Description": item.description || "-"
                }));

                const worksheet = XLSX.utils.json_to_sheet(data);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
                XLSX.writeFile(workbook, `Stock_Report_${new Date().getTime()}.xlsx`);
                toast.success("Excel downloaded successfully");
            } else toast.error("Failed to export data");
        } catch (error) {
            console.error(error);
            toast.error("An error occurred during export");
        }
    };

    const getTypeBadge = (type) => {
        const t = (type || "").toLowerCase().trim();
        switch (t) {
            case "initial": return <span className="badge bg-info-focus text-info-main px-24 py-4 radius-4">Initial</span>;
            case "order": return <span className="badge bg-danger-focus text-danger-main px-24 py-4 radius-4">Order</span>;
            case "stock_update": return <span className="badge bg-success-focus text-success-main px-24 py-4 radius-4">Physical</span>;
            case "purchase": return <span className="badge bg-primary-focus text-primary-main px-24 py-4 radius-4">Purchase</span>;
            case "return": return <span className="badge bg-success-focus text-success-main px-24 py-4 radius-4">Return</span>;
            case "cancel": return <span className="badge bg-warning-focus text-warning-main px-24 py-4 radius-4">Cancel</span>;
            case "payment_failed":
            case "payment-failed":
                return <span className="badge bg-danger-focus text-danger-main px-24 py-4 radius-4">Payment Failed</span>;
            default: {
                const label = (type || "Manual").replace(/_/g, " ").replace(/-/g, " ");
                const formatted = label.charAt(0).toUpperCase() + label.slice(1);
                return <span className="badge bg-neutral-200 text-neutral-800 px-24 py-4 radius-4">{formatted}</span>;
            }
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                    <h6 className="text-primary-600 pb-2 mb-0">Stock Reports</h6>
                </div>

                <div className="row g-3 align-items-end">
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>From Date</label>
                        <StandardDatePicker name="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-control radius-8" />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>To Date</label>
                        <StandardDatePicker name="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-control radius-8" />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>Product (Optional)</label>
                        <Select
                            options={productOptions}
                            value={selectedProduct}
                            onChange={setSelectedProduct}
                            onInputChange={(inputValue, { action }) => {
                                if (action === "input-change") {
                                    setProductSearch(inputValue);
                                } else if (action === "input-blur" || action === "menu-close") {
                                    setProductSearch("");
                                }
                            }}
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            filterOption={() => true}
                            isLoading={loadingProducts}
                            placeholder="All Products"
                            styles={selectStyles()}
                            isClearable
                        />
                    </div>
                    <div className="col-xl-auto col-md-3 col-sm-6 d-flex align-items-end gap-2">
                        {/* <button type="button" onClick={() => { setCurrentPage(0); fetchReport(); }} className="btn btn-primary d-flex align-items-center gap-2 radius-8 h-40-px" style={{ fontSize: '14px' }}>Filter</button> */}
                        <button type="button" onClick={handleClearFilters} className="btn btn-outline-danger d-flex align-items-center gap-2 radius-8 h-40-px text-nowrap">
                            <Icon icon="solar:filter-remove-bold-duotone" fontSize={20} /> Clear
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Date</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Product</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Variant</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Prev Stock</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Change</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Curr Stock</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Type</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Reference</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Created By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (<tr><td colSpan="9" className="text-center py-4">Data loading...</td></tr>) : reportData.length > 0 ? (
                                reportData.map((data, index) => (
                                    <tr key={index}>
                                        <td>{currentPage * rowsPerPage + index + 1}</td>
                                        <td>{data.createdAt ? new Date(data.createdAt).toLocaleString() : "-"}</td>
                                        <td>{data.productName}</td>
                                        <td>{getVariantText(data)}</td>
                                        <td>{data.previousStock}</td>
                                        <td className={data.quantity > 0 ? "text-success-main fw-bold" : "text-danger-main fw-bold"}>
                                            {data.quantity > 0 ? `+${data.quantity}` : data.quantity}
                                        </td>
                                        <td>{data.currentStock}</td>
                                        <td>{getTypeBadge(data.type)}</td>
                                        <td>{getReferenceText(data)}</td>
                                        <td><span className="text-secondary-light fw-medium">{data.createdByName || "System"}</span></td>
                                    </tr>
                                ))
                            ) : (<tr><td colSpan="9" className="text-center py-4">No reports found.</td></tr>)}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-24">
                    <div>
                        {hasPermission("Stock Reports") && reportData.length > 0 && (
                            <button type="button" className="btn btn-primary radius-8 px-20 py-11 d-flex align-items-center gap-2" onClick={handleExportExcel}>
                                <Icon icon="lucide:download" className="text-xl" /> Export Excel
                            </button>
                        )}
                    </div>
                    <div className="flex-grow-1">
                        <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }} totalRecords={totalRecords} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockReportsLayer;
